import { useEffect, useMemo, useState } from "react";
import { BookOpen, CheckCircle2, Clock, Plus, ArrowUpDown, AlertTriangle } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// Sprint 2H Phase C — Legislation versions admin.
//
// Backend contract (from /app/backend/admin_l1.py):
//   GET   /api/admin/legislation-versions
//   POST  /api/admin/legislation-versions             (jurisdiction MUST be NSW|QLD)
//   PATCH /api/admin/legislation-versions/{id}/supersede   (no body — server sets ts + fans out reverification flags)
//
// UI decisions:
//   • Filter chips list all 8 AU jurisdictions per the app-wide expansion set,
//     even though backend only accepts NSW/QLD writes today. Chips for jurisdictions
//     without rows still work — they filter to empty and inform the admin.
//   • The Supersede action does NOT accept a client-supplied effective_from — the
//     backend records `superseded_at = server now()` and marks confirmations +
//     compliance rows referencing this version for reverification. We reflect
//     that server behaviour honestly in the confirm dialog instead of collecting
//     a value the backend would ignore.

const AU_STATES = ["NSW", "QLD", "VIC", "WA", "SA", "TAS", "ACT", "NT"];
const WRITABLE_STATES = ["NSW", "QLD"];

export default function AdminLegislationVersions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [sortDesc, setSortDesc] = useState(true); // by effective_from
  const [creating, setCreating] = useState(false);
  const [pendingSupersede, setPendingSupersede] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Route guard — non-admins bounce. Backend also enforces via require_admin_dep
  // on the write routes; the list route is authenticated but not admin-only.
  useEffect(() => {
    if (user && user.system_role !== "platform_admin") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/legislation-versions");
      setRows(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const base = filter === "ALL" ? rows : rows.filter((r) => r.jurisdiction === filter);
    return [...base].sort((a, b) => {
      const av = a.effective_from || "";
      const bv = b.effective_from || "";
      return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv);
    });
  }, [rows, filter, sortDesc]);

  const stats = useMemo(() => {
    if (!rows) return { current: 0, jurisdictions: 0 };
    const current = rows.filter((r) => !r.superseded_at);
    const jurisdictions = new Set(current.map((r) => r.jurisdiction));
    return { current: current.length, jurisdictions: jurisdictions.size };
  }, [rows]);

  const counts = useMemo(() => {
    const c = { ALL: rows?.length || 0 };
    AU_STATES.forEach((s) => { c[s] = (rows || []).filter((r) => r.jurisdiction === s).length; });
    return c;
  }, [rows]);

  const doSupersede = async () => {
    if (!pendingSupersede) return;
    setSubmitting(true);
    try {
      await api.patch(`/admin/legislation-versions/${pendingSupersede.legislation_version_id}/supersede`);
      toast.success(`Superseded ${pendingSupersede.jurisdiction} ${pendingSupersede.version_label}`);
      setPendingSupersede(null);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  if (user && user.system_role !== "platform_admin") {
    return null; // navigate() effect will redirect
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8" data-testid="admin-legislation-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <BookOpen size={22} style={{ color: "var(--blue)" }} />
            <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }} data-testid="admin-legislation-heading">
              Legislation Versions
            </h1>
          </div>
          <Button
            onClick={() => setCreating(true)}
            style={{ background: "var(--blue)", color: "white" }}
            data-testid="admin-legislation-create-btn"
          >
            <Plus size={14} className="mr-1.5" /> New version
          </Button>
        </div>

        <p className="text-sm mb-3" style={{ color: "var(--slate)" }}>
          Current legislation registry. Regulated write actions (levies, meeting
          notices, minutes publish) pin every confirmation to the current version
          for the scheme&apos;s jurisdiction. Superseding a version flags all downstream
          confirmations for re-verification.
        </p>

        {/* Stats header */}
        <div
          className="rounded-lg border px-4 py-3 mb-4 flex items-center gap-6 text-sm bg-white"
          style={{ borderColor: "var(--border)" }}
          data-testid="admin-legislation-stats"
        >
          <div>
            <span className="text-2xl font-bold" style={{ color: "var(--navy)" }}>{stats.current}</span>
            <span className="ml-1.5 text-xs uppercase tracking-wider" style={{ color: "var(--slate)" }}>
              current version{stats.current === 1 ? "" : "s"}
            </span>
          </div>
          <div className="h-8 w-px" style={{ background: "var(--border)" }} />
          <div>
            <span className="text-2xl font-bold" style={{ color: "var(--navy)" }}>{stats.jurisdictions}</span>
            <span className="ml-1.5 text-xs uppercase tracking-wider" style={{ color: "var(--slate)" }}>
              jurisdictions covered
            </span>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-legislation-filter-chips">
          <FilterChip label={`All (${counts.ALL})`} active={filter === "ALL"} onClick={() => setFilter("ALL")} testid="chip-ALL" />
          {AU_STATES.map((s) => (
            <FilterChip
              key={s}
              label={`${s} (${counts[s]})`}
              active={filter === s}
              onClick={() => setFilter(s)}
              testid={`chip-${s}`}
              dim={counts[s] === 0}
            />
          ))}
        </div>

        {/* Table */}
        <div className="rounded-xl bg-white border overflow-hidden" style={{ borderColor: "var(--border)" }}>
          {!filtered ? (
            <div className="p-5 space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm" style={{ color: "var(--slate)" }} data-testid="admin-legislation-empty">
              No legislation versions on file for this filter.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead style={{ background: "#f8fafc" }}>
                <tr className="text-[11px] uppercase tracking-wider" style={{ color: "var(--slate)" }}>
                  <th className="text-left px-4 py-2 font-semibold">Jurisdiction</th>
                  <th className="text-left px-4 py-2 font-semibold">Act</th>
                  <th className="text-left px-4 py-2 font-semibold">Version</th>
                  <th className="text-left px-4 py-2 font-semibold">
                    <button
                      className="inline-flex items-center gap-1 hover:opacity-70"
                      onClick={() => setSortDesc((d) => !d)}
                      data-testid="sort-effective-from"
                    >
                      Effective from <ArrowUpDown size={11} />
                    </button>
                  </th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                  <th className="text-left px-4 py-2 font-semibold">Superseded at</th>
                  <th className="text-right px-4 py-2 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => (
                  <tr
                    key={v.legislation_version_id}
                    className="border-t"
                    style={{ borderColor: "var(--border)" }}
                    data-testid={`legislation-row-${v.legislation_version_id}`}
                  >
                    <td className="px-4 py-3 font-semibold" style={{ color: "var(--navy)" }}>{v.jurisdiction}</td>
                    <td className="px-4 py-3" style={{ color: "var(--slate)" }}>{v.act_name || "—"}</td>
                    <td className="px-4 py-3" style={{ color: "var(--navy)" }}>{v.version_label || "—"}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--slate)" }}>{v.effective_from || "—"}</td>
                    <td className="px-4 py-3">
                      {v.superseded_at ? (
                        <span className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--slate)" }}>
                          <Clock size={12} /> superseded
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 text-xs font-semibold"
                          style={{ color: "#166534" }}
                          data-testid={`current-badge-${v.legislation_version_id}`}
                        >
                          <CheckCircle2 size={12} /> current
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--slate)" }}>
                      {v.superseded_at ? String(v.superseded_at).slice(0, 10) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!v.superseded_at && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPendingSupersede(v)}
                          data-testid={`supersede-btn-${v.legislation_version_id}`}
                        >
                          Supersede
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <CreateVersionDialog
          open={creating}
          onOpenChange={setCreating}
          onCreated={async () => { setCreating(false); await load(); }}
        />

        <SupersedeDialog
          version={pendingSupersede}
          submitting={submitting}
          onCancel={() => setPendingSupersede(null)}
          onConfirm={doSupersede}
        />
      </div>
    </AppShell>
  );
}

function FilterChip({ label, active, onClick, testid, dim }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="text-xs font-medium px-3 py-1.5 rounded-full border transition-all"
      style={{
        background: active ? "var(--navy)" : "white",
        color: active ? "white" : "var(--slate)",
        borderColor: active ? "var(--navy)" : "var(--border)",
        opacity: dim && !active ? 0.55 : 1,
      }}
    >
      {label}
    </button>
  );
}

function CreateVersionDialog({ open, onOpenChange, onCreated }) {
  const [jurisdiction, setJurisdiction] = useState("NSW");
  const [actName, setActName] = useState("");
  const [versionLabel, setVersionLabel] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setJurisdiction("NSW"); setActName(""); setVersionLabel("");
    setEffectiveFrom(""); setAdminNotes("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/admin/legislation-versions", {
        jurisdiction,
        act_name: actName,
        version_label: versionLabel,
        effective_from: effectiveFrom || undefined,
        admin_notes: adminNotes || undefined,
      });
      toast.success(`Added ${jurisdiction} ${versionLabel || "version"}`);
      reset();
      await onCreated?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg" data-testid="admin-legislation-create-modal">
        <DialogHeader>
          <DialogTitle style={{ color: "var(--navy)" }}>Add legislation version</DialogTitle>
          <DialogDescription className="text-xs">
            Currently accepted jurisdictions: {WRITABLE_STATES.join(", ")}. The registry
            covers all 8 Australian jurisdictions but the two-state pilot only writes NSW / QLD.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Jurisdiction</Label>
            <Select value={jurisdiction} onValueChange={setJurisdiction}>
              <SelectTrigger data-testid="create-jurisdiction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WRITABLE_STATES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs" htmlFor="act-name">Act name</Label>
            <Input
              id="act-name"
              value={actName}
              onChange={(e) => setActName(e.target.value)}
              placeholder="e.g. Strata Schemes Management Act 2015"
              data-testid="create-act-name"
            />
          </div>
          <div>
            <Label className="text-xs" htmlFor="version-label">Version label</Label>
            <Input
              id="version-label"
              value={versionLabel}
              onChange={(e) => setVersionLabel(e.target.value)}
              placeholder="e.g. 2025-07 amendment set"
              data-testid="create-version-label"
            />
          </div>
          <div>
            <Label className="text-xs" htmlFor="effective-from">Effective from (YYYY-MM-DD)</Label>
            <Input
              id="effective-from"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              data-testid="create-effective-from"
            />
          </div>
          <div>
            <Label className="text-xs" htmlFor="admin-notes">Source citation / notes</Label>
            <Input
              id="admin-notes"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Legislation.gov.au URL or amendment reference"
              data-testid="create-admin-notes"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => { reset(); onOpenChange(false); }} data-testid="create-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !actName || !versionLabel}
              style={{ background: "var(--blue)", color: "white" }}
              data-testid="create-submit"
            >
              {submitting ? "Adding…" : "Add version"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SupersedeDialog({ version, submitting, onCancel, onConfirm }) {
  return (
    <Dialog open={!!version} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="admin-legislation-supersede-modal">
        <DialogHeader>
          <DialogTitle style={{ color: "var(--navy)" }}>
            Supersede {version?.jurisdiction} — {version?.version_label}?
          </DialogTitle>
          <DialogDescription className="text-xs">
            This is a one-way action. The server marks this version superseded at
            the current timestamp — no override.
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-md p-3 text-xs flex gap-2"
          style={{ background: "#fef3c7", color: "#78350f", borderLeft: "3px solid #f59e0b" }}
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>
            <strong>Downstream effect:</strong> every confirmation row and compliance
            item still pinned to this version will be flagged
            <code className="mx-1 px-1 rounded" style={{ background: "#fde68a" }}>requires_reverification = true</code>
            with the current timestamp. Chairs will see re-verify prompts on their
            audit trail until a new current version is added and re-confirmed.
          </div>
        </div>

        <p className="text-xs" style={{ color: "var(--slate)" }}>
          Best practice: add the replacement version first, then supersede the old one.
        </p>

        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} data-testid="supersede-cancel">Cancel</Button>
          <Button
            onClick={onConfirm}
            disabled={submitting}
            style={{ background: "#991b1b", color: "white" }}
            data-testid="supersede-confirm"
          >
            {submitting ? "Superseding…" : "Supersede"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
